import { message, Modal } from 'antd';
import type { ColumnGroupType, ColumnsType, ColumnType } from 'antd/es/table';
import type { RangePickerProps } from 'antd/lib/date-picker';
import axios, { type AxiosRequestConfig, AxiosResponse } from 'axios';
import moment, { type Moment } from 'moment';
import type { Fetcher, KeyedMutator } from 'swr';

import { DEFAULT_OPTION, WEEK } from './constants';
import type {
  ActionButton,
  FilterOptions,
  InspectionTableResponse,
  RecordInTable,
} from './types';

const { confirm } = Modal;

export const renderCycleColumnData = (_: any, record: RecordInTable) => {
  const { type, days } = record.cycle;
  switch (type) {
    case 'month':
      return '每月' + days.join('日, ') + '日';
    case 'week':
      return '每週' + days.map((day) => WEEK[day]).join(', ');
    default:
      return '';
  }
};

/**
 * 渲染表格操作欄位的函數型別
 *
 * 用於根據給定的紀錄 (record) 動態生成操作按鈕
 * */
type ActionRenderer = (record: RecordInTable) => JSX.Element | null;

/**
 * 包括操作欄位的columns的型別
 */
type ColumnsWithAction = (
  | ColumnGroupType<RecordInTable>
  | ColumnType<RecordInTable>
)[];

/**
 * @param defaultColumns 原有的欄位columns
 * @param renderAction 用於渲染表格的操作欄位
 * @returns 包含操作欄位的columns
 */
export const getTableColumns = (
  defaultColumns: ColumnsType<RecordInTable>,
  renderAction: ActionRenderer
): ColumnsWithAction => {
  const columns = [...defaultColumns];
  const actionColumn: ColumnType<RecordInTable> = {
    title: '操作',
    dataIndex: 'action',
    key: 'action',
    fixed: 'right',
    render: (_, record) => renderAction(record),
  };
  columns.push(actionColumn);
  return columns;
};

export const fetchRouteFilterOptions = async (url: string) => {
  try {
    const { data }: AxiosResponse<FilterOptions> = await axios({
      url,
      method: 'GET',
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

export const getSortedRouteOptions = (routeNameList: string[]) => {
  const routeOptions = routeNameList.map((name) => ({
    label: name,
    value: name,
  }));
  routeOptions.sort((a, b) => a.label.localeCompare(b.label));
  const options = [DEFAULT_OPTION, ...routeOptions];
  return options;
};

export const checkIsValidPeriod = (
  period: RangePickerProps['value'] | null
): period is [Moment, Moment] => {
  return (
    Array.isArray(period) &&
    period.length === 2 &&
    period[0] instanceof moment &&
    period[1] instanceof moment
  );
};

const getPeriodTimestamps = (period: RangePickerProps['value'] | null) => {
  const isValidPeriod = checkIsValidPeriod(period);
  const values = isValidPeriod
    ? [period[0].valueOf(), period[1].valueOf()]
    : undefined;
  return values;
};

type RouteListFetcherArgs = {
  url: string;
  route: string;
  period: RangePickerProps['value'] | null;
  page: number;
  pageSize: number;
};

const ACTION_BUTTONS: ActionButton[] = [
  {
    label: '查看',
    type: 'secondary',
  },
  {
    label: '編輯',
    type: 'normal',
  },
  {
    label: '刪除',
    type: 'primary',
  },
];

export const fetchInspectionRouteList: Fetcher<
  InspectionTableResponse | undefined,
  RouteListFetcherArgs
> = async (arg) => {
  const period = getPeriodTimestamps(arg.period);

  try {
    const { data: inspectionRoute }: AxiosResponse<InspectionTableResponse> =
      await axios({
        url: arg.url,
        method: 'GET',
        headers: {
          authorization: localStorage.getItem('feathers-jwt'),
        },
        params: {
          ...arg,
          route: arg.route === '全部' ? undefined : arg.route,
          period,
        },
      });

    const routeList: RecordInTable[] = inspectionRoute.data.map(
      (routeData) => ({
        ...routeData,
        action: ACTION_BUTTONS,
      })
    );

    const newInspectionRoute = {
      totalPages: inspectionRoute.totalPages,
      data: routeList,
    };
    return newInspectionRoute;
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const deleteRoute = async (
  routeId: string,
  mutate: KeyedMutator<InspectionTableResponse | undefined>
) => {
  try {
    await axios({
      url: `/ezcon/api/machineInspectionPlans/${routeId}`,
      method: 'DELETE',
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
    });
    message.success('刪除成功!');
    mutate();
  } catch (error) {
    console.error(error);
    message.error('刪除失敗!');
  }
};

export const handleDeleteConfirm = (
  routeId: string,
  mutate: KeyedMutator<InspectionTableResponse | undefined>
) => {
  confirm({
    title: '確定要刪除路線嗎？',
    content: '刪除後無法復原!!',
    centered: true,
    onOk: async () => {
      await deleteRoute(routeId, mutate);
    },
  });
};

export const showQrcode = async (equipmentId?: string) => {
  const axiosConfig: AxiosRequestConfig = {
    url: '/ezcon/api/machineInspectionPlans/downLoadQRCode',
    method: 'GET',
    headers: {
      authorization: localStorage.getItem('feathers-jwt'),
    },
    params: {
      equipmentId: equipmentId || undefined,
    },
    responseType: 'blob',
  };

  try {
    const { data: pdfFile } = await axios(axiosConfig);
    const blob = new Blob([pdfFile], { type: 'application/pdf' });
    const pdfUrl = URL.createObjectURL(blob);
    window.open(pdfUrl, '_blank');
    URL.revokeObjectURL(pdfUrl);
  } catch (error) {
    console.error(error);
    message.error('QRCode下載失敗!');
  }
};
